import React from 'react';

import { Icon } from '../Icon/Icon';
import { Tooltip } from '../Tooltip';

import type { IButtonProps } from './Button.type';
import { IconName } from '../Icon/Icon.type';

export const Button: React.FC<IButtonProps> = (
  {
    label,
    onClick,
    size,
    variant,
    disabled,
    loading,
    type,
    className,
    ariaLabel,
    title,
    tooltipDelay,
    tooltipPosition,
    onMouseEnter,
    onMouseLeave,
  }: IButtonProps) => {

  const colorMapper =
  {
    "primary": "bg-bg-brand-solid text-text-white hover:bg-bg-brand-solid-hover active:bg-bg-brand-solid shadow-bg-v2-xs",
    "secondary": "bg-bg-secondary-solid text-text-white hover:bg-bg-secondary-hover active:bg-bg-secondary-solid shadow-bg-v2-xs",
    "outline": "border border-border-primary text-text-secondary bg-bg-primary hover:bg-bg-primary-hover active:bg-bg-primary-hover",
    "ghost": "bg-transparent text-text-fg-primary hover:bg-bg-primary-hover active:bg-bg-primary-hover",
    "link": "bg-transparent text-text-brand-primary hover:text-text-brand-secondary underline-offset-4 hover:underline p-0 h-auto",
    "success": "bg-bg-success-solid text-text-white hover:bg-bg-success-solid-hover active:bg-bg-success-solid shadow-bg-v2-xs",
    "error": "bg-bg-error-solid text-text-white hover:bg-bg-error-solid-hover active:bg-bg-error-solid shadow-bg-v2-xs",
    "brand": "bg-bg-brand-primary text-text-brand-secondary hover:bg-bg-brand-secondary active:bg-bg-brand-secondary",
  };

  const sizeMapper =
  {
    "small": "text-bg-v2-sm px-bg-v2-lg py-bg-v2-md gap-bg-v2-md",
    "medium": "text-bg-v2-md px-bg-v2-xl py-bg-v2-lg gap-bg-v2-lg",
    "large": "text-bg-v2-lg px-bg-v2-2xl py-bg-v2-2xl gap-bg-v2-2xl",
    "very-small": "text-xs px-1 py-1 gap-1",
  };

  const buttonElement = (
    <button
      className={`${colorMapper[variant ?? "primary"]} ${sizeMapper[size ?? "medium"]} rounded-lg font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-40 inline-flex items-center justify-center ${className ?? ""}`}
      type={type}
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled ?? loading}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {loading ? (
        <>
          <Icon name={IconName.Loading} size="md" spin />
          <span>{label}</span>
        </>
      ) : (
        <>{label}</>
      )}
    </button>
  );

  // Wrap with Tooltip if title is provided
  if (title) {
    return (
      <Tooltip content={title} delay={tooltipDelay ?? 200} position={tooltipPosition ?? 'top'}>
        {buttonElement}
      </Tooltip>
    );
  }

  return buttonElement;
};